import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FolderKanban, CheckSquare, Building2, Users, TrendingUp, AlertCircle, DollarSign } from 'lucide-react';

const statusColor = { planning: 'badge-blue', in_progress: 'badge-yellow', review: 'badge-purple', completed: 'badge-green', on_hold: 'badge-gray' };

// Progress donut chart (SVG)
const DonutChart = ({ value, color, size = 90, thickness = 10, label }) => {
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const circumference = 2 * Math.PI * r;
  const filled = Math.max(0, Math.min(100, value)) / 100 * circumference;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--border)" strokeWidth={thickness} />
          <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeWidth={thickness}
            strokeDasharray={`${filled} ${circumference - filled}`}
            strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cx})`}
            style={{ transition: 'stroke-dasharray 0.6s ease' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700 }}>
          {value}%
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>{label}</div>
    </div>
  );
};

// Horizontal bar chart row  clickable
const HBar = ({ label, value, max, color, count, onClick }) => (
  <div className="hbar-row" onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default', borderRadius: 6, padding: '4px 6px', margin: '0 -6px', transition: 'background 0.15s' }}
    onMouseEnter={e => onClick && (e.currentTarget.style.background = 'var(--bg-hover)')}
    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
    <div className="hbar-label">
      <span>{label}</span>
      <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>{count}</span>
    </div>
    <div className="hbar-track">
      <div className="hbar-fill" style={{ width: `${max ? (value / max) * 100 : 0}%`, background: color }} />
    </div>
  </div>
);

const AdminDashboard = () => {
  const [data, setData] = useState({ projects: [], tasks: [], clients: [], users: [], invoices: [], quotations: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([axios.get('/api/projects'), axios.get('/api/tasks'), axios.get('/api/clients'), axios.get('/api/users'), axios.get('/api/invoices'), axios.get('/api/quotations')])
      .then(([p, t, c, u, i, q]) => setData({ projects: p.data.data, tasks: t.data.data, clients: c.data.data, users: u.data.data, invoices: i.data.data, quotations: q.data.data }))
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  const { projects, tasks, clients, users, invoices, quotations } = data;
  const activeProjects = projects.filter(p => p.status === 'in_progress').length;
  const completedProjects = projects.filter(p => p.status === 'completed').length;
  const doneTasks = tasks.filter(t => t.status === 'done').length;
  const totalProgress = projects.length ? Math.round(projects.reduce((s, p) => s + p.progress, 0) / projects.length) : 0;
  const taskCompletion = tasks.length ? Math.round((doneTasks / tasks.length) * 100) : 0;
  const maxProjects = Math.max(1, ...['planning', 'in_progress', 'review', 'completed', 'on_hold'].map(s => projects.filter(p => p.status === s).length));
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const weekEnd = new Date(now); weekEnd.setDate(now.getDate() + 7);
  const dueSoonTasks = tasks.filter(t => t.status !== 'done' && t.dueDate && new Date(t.dueDate) <= weekEnd).slice(0, 6);
  const overdueInvoices = invoices.filter(i => i.status === 'overdue');
  const acceptedQuotes = quotations.filter(q => ['accepted', 'converted'].includes(q.status)).length;
  const conversionRate = quotations.length ? Math.round((acceptedQuotes / quotations.length) * 100) : 0;
  const quoteValue = quotations.reduce((sum, q) => sum + Number(q.total || 0), 0);
  const paidValue = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + Number(i.total || 0), 0);

  return (
    <div className="page-content">
      {/* Stats */}
      <div className="stats-grid">
        {[
          { label: 'Total Clients', value: clients.length, icon: <Building2 size={22} />, color: '#3b82f6' },
          { label: 'Active Projects', value: activeProjects, icon: <FolderKanban size={22} />, color: '#a855f7' },
          { label: 'Pending Tasks', value: tasks.filter(t => t.status !== 'done').length, icon: <CheckSquare size={22} />, color: '#eab308' },
          { label: 'Team Members', value: users.filter(u => u.role !== 'client').length, icon: <Users size={22} />, color: '#22c55e' },
          { label: 'Overdue Invoices', value: overdueInvoices.length, icon: <DollarSign size={22} />, color: '#ef4444' },
          { label: 'Quotations', value: quotations.length, icon: <DollarSign size={22} />, color: '#7D39EB' },
          { label: 'Conversion Rate', value: `${conversionRate}%`, icon: <TrendingUp size={22} />, color: '#C6FF33' },
          { label: 'Quote Value', value: quoteValue.toLocaleString(), icon: <DollarSign size={22} />, color: '#C6FF33' },
          { label: 'Paid Revenue', value: paidValue.toLocaleString(), icon: <DollarSign size={22} />, color: '#C6FF33' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-icon" style={{ color: s.color }}>{s.icon}</div>
            <div className="stat-value">{s.value}</div>
            <div className="stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid-2" style={{ marginBottom: 24 }}>
        <div className="card">
          <div className="card-title mb-16">Due This Week</div>
          {dueSoonTasks.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <AlertCircle size={15} color={new Date(t.dueDate) < now ? '#ef4444' : '#eab308'} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{t.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.project?.name} · Due {new Date(t.dueDate).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
          {!dueSoonTasks.length && <div style={{ padding: '14px 0', color: 'var(--text-muted)', fontSize: 13 }}>No urgent due dates this week</div>}
        </div>
        <div className="card">
          <div className="card-title mb-16">Overdue Invoices</div>
          {overdueInvoices.slice(0, 6).map(i => (
            <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <DollarSign size={15} color="#ef4444" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{i.invoiceNumber}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{i.client?.companyName} · {i.currency} {Number(i.total || 0).toLocaleString()}</div>
              </div>
            </div>
          ))}
          {!overdueInvoices.length && <div style={{ padding: '14px 0', color: 'var(--text-muted)', fontSize: 13 }}>No overdue invoices</div>}
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {/* Project Status */}
        <div className="chart-card">
          <div className="chart-title">Project Status</div>
          {[
            { label: 'Planning', key: 'planning', color: '#3b82f6' },
            { label: 'In Progress', key: 'in_progress', color: '#eab308' },
            { label: 'Review', key: 'review', color: '#a855f7' },
            { label: 'Completed', key: 'completed', color: '#22c55e' },
            { label: 'On Hold', key: 'on_hold', color: '#666' },
          ].map(({ label, key, color }) => {
            const count = projects.filter(p => p.status === key).length;
            return <HBar key={key} label={label} value={count} max={maxProjects} color={color} count={count}
              onClick={() => navigate(`/projects?filter=${key}`)} />;
          })}
        </div>

        {/* Progress Donuts */}
        <div className="chart-card">
          <div className="chart-title">Overall Progress</div>
          <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', paddingTop: 8 }}>
            <DonutChart value={totalProgress} color="#a855f7" label="Avg Project Progress" />
            <DonutChart value={taskCompletion} color="#22c55e" label="Task Completion" />
            <DonutChart value={projects.length ? Math.round((completedProjects / projects.length) * 100) : 0} color="#3b82f6" label="Projects Done" />
          </div>
        </div>
      </div>

      {/* Recent Projects & Tasks */}
      <div className="grid-2">
        <div className="card">
          <div className="card-title mb-16">Recent Projects</div>
          {projects.slice(0, 6).map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{p.client?.companyName}</div>
              </div>
              <div style={{ width: 80 }}>
                <div className="progress-bar"><div className="progress-fill" style={{ width: `${p.progress}%` }} /></div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 3 }}>{p.progress}%</div>
              </div>
              <span className={`badge ${statusColor[p.status] || 'badge-gray'}`} style={{ fontSize: 11 }}>{p.status?.replace('_', ' ')}</span>
            </div>
          ))}
          {!projects.length && <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)' }}>No projects yet</div>}
        </div>
        <div className="card">
          <div className="card-title mb-16">Recent Tasks</div>
          {tasks.slice(0, 6).map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{t.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{t.project?.name} · {t.assignee?.name || 'Unassigned'}</div>
              </div>
              <span className={`badge ${t.priority === 'urgent' ? 'badge-red' : t.priority === 'high' ? 'badge-yellow' : 'badge-gray'}`} style={{ fontSize: 11 }}>{t.priority}</span>
            </div>
          ))}
          {!tasks.length && <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-muted)' }}>No tasks yet</div>}
        </div>
      </div>
    </div>
  );
};

const ClientDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get('/api/projects').then(r => setProjects(r.data.data)).catch(console.error).finally(() => setLoading(false)); }, []);

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading...</div>;

  const completedCount = projects.filter(p => p.status === 'completed').length;
  const avgProgress = projects.length ? Math.round(projects.reduce((s, p) => s + p.progress, 0) / projects.length) : 0;

  return (
    <div className="page-content">
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-icon" style={{ color: '#3b82f6' }}><FolderKanban size={22} /></div><div className="stat-value">{projects.length}</div><div className="stat-label">Total Projects</div></div>
        <div className="stat-card"><div className="stat-icon" style={{ color: '#a855f7' }}><TrendingUp size={22} /></div><div className="stat-value">{projects.filter(p => p.status === 'in_progress').length}</div><div className="stat-label">In Progress</div></div>
        <div className="stat-card"><div className="stat-icon" style={{ color: '#22c55e' }}><CheckSquare size={22} /></div><div className="stat-value">{completedCount}</div><div className="stat-label">Completed</div></div>
        <div className="stat-card"><div className="stat-icon" style={{ color: '#eab308' }}><TrendingUp size={22} /></div><div className="stat-value">{avgProgress}%</div><div className="stat-label">Avg Progress</div></div>
      </div>

      {/* Progress Donut */}
      {projects.length > 0 && (
        <div className="charts-grid" style={{ gridTemplateColumns: '1fr' }}>
          <div className="chart-card">
            <div className="chart-title">My Projects Progress</div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', paddingTop: 8 }}>
              <DonutChart value={avgProgress} color="#a855f7" label="Overall Progress" />
              <DonutChart value={projects.length ? Math.round((completedCount / projects.length) * 100) : 0} color="#22c55e" label="Completion Rate" />
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-title mb-16">My Projects</div>
        {projects.map(p => (
          <div key={p.id} className="card card-sm mb-16" style={{ background: 'var(--bg-secondary)' }}>
            <div className="flex-between mb-16">
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{p.type?.replace('_', ' ')} · Due: {p.endDate ? new Date(p.endDate).toLocaleDateString() : 'TBD'}</div>
              </div>
              <span className={`badge ${statusColor[p.status] || 'badge-gray'}`}>{p.status?.replace('_', ' ')}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-muted)', marginBottom: 6 }}><span>Progress</span><span>{p.progress}%</span></div>
            <div className="progress-bar" style={{ height: 8 }}><div className="progress-fill" style={{ width: `${p.progress}%` }} /></div>
            {p.description && <p style={{ marginTop: 12, fontSize: 13, color: 'var(--text-secondary)' }}>{p.description}</p>}
          </div>
        ))}
        {!projects.length && <div className="empty-state"><FolderKanban size={40} /><p>No projects assigned yet</p></div>}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  if (user?.role === 'client') return <ClientDashboard />;
  return <AdminDashboard />;
};

export default Dashboard;
